import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Tooltip as UiTooltip } from '../../../../Tooltip';
import styles from './federationSummaryCard.module.css';
import { useHistory } from 'react-router-dom';
// import ChartDownloadIcon from '../../../../../assets/chartDownloadIcon.svg';
import DownIcon from '../../../../../assets/DownIcon';
import ExpandIcon from '../../../../../assets/ExpandIcon';
import SimplePlusIcon from '../../../../../assets/SimplePlusIcon';

export interface LineConfig {
  key: string;
  name: string;
  color: string;
}

interface FederationSummaryCardProps {
  title: string;
  secondDropdownLabel?: string;
  data: Record<string, any>[];
  lines: LineConfig[];
  xAxisKey: string;
  yTickFormatter?: (value: number) => string;
  isLoading?: boolean;
  hasError?: boolean;
  dateRange?: { start: string; end: string };
  onMonthClick?: (monthLabel: string, metric: 'total' | 'query', selectedKeys: string[]) => void;
  queryMetricLabel?: string;
}

const METRICS = [
  { label: 'Audience Volume', key: 'total' as const },
  { label: 'Queries Executed', key: 'query' as const },
];

type MetricKey = 'total' | 'query';

/** Native title tooltip only when the label is visually truncated. */
function useTruncationTooltip(text: string) {
  const ref = useRef<HTMLSpanElement>(null);
  const [title, setTitle] = useState<string | undefined>();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const sync = () => {
      setTitle(el.scrollWidth > el.clientWidth + 1 ? text : undefined);
    };

    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return { ref, title };
}

const ErrorState: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 160, gap: 6 }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#F59E0B" strokeWidth="1.5"/>
      <path d="M12 8v4.5" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="0.875" fill="#F59E0B"/>
    </svg>
    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
      Data unavailable.<br/>Please try refreshing.
    </span>
  </div>
);

const EmptyState: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 160, gap: 6 }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#CBD5E1" strokeWidth="1.5"/>
      <path d="M8 12h8" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
      No data available for the selected period.
    </span>
  </div>
);

const FederationSummaryCard: React.FC<FederationSummaryCardProps> = ({
  title,
  secondDropdownLabel = 'Federations',
  data,
  lines,
  xAxisKey,
  yTickFormatter,
  isLoading,
  hasError,
  onMonthClick,
  dateRange,
  queryMetricLabel,
}) => {
  const history = useHistory();

  const metrics = METRICS.map(m =>
    m.key === 'query' && queryMetricLabel ? { ...m, label: queryMetricLabel } : m,
  );

  // Single-select metric
  const [metric, setMetric] = useState<MetricKey>('total');
  const [metricDropdownOpen, setMetricDropdownOpen] = useState(false);
  const metricDropdownRef = useRef<HTMLDivElement>(null);

  // Federation multiselect
  const [selectedFedKeys, setSelectedFedKeys] = useState<Set<string>>(() => new Set(lines.map(l => l.key)));
  const [fedDropdownOpen, setFedDropdownOpen] = useState(false);
  const fedDropdownRef = useRef<HTMLDivElement>(null);

  // Sync selectedFedKeys when lines change — preserve user's selection for keys that still exist
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedFedKeys(prev => {
      const newKeySet = new Set(lines.map(l => l.key));
      const preserved = new Set(Array.from(prev).filter(k => newKeySet.has(k)));
      return preserved.size > 0 ? preserved : newKeySet;
    });
  }, [lines.map(l => l.key).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (metricDropdownRef.current && !metricDropdownRef.current.contains(e.target as Node)) setMetricDropdownOpen(false);
      if (fedDropdownRef.current && !fedDropdownRef.current.contains(e.target as Node)) setFedDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll-fade for color key strip
  const colorKeyRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = colorKeyRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 4);
    setFadeRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => { updateFades(); }, [lines, selectedFedKeys, updateFades]);

  const allFedSelected = lines.length > 0 && lines.every(l => selectedFedKeys.has(l.key));
  const someFedSelected = !allFedSelected && selectedFedKeys.size > 0;

  const toggleFed = (key: string) => {
    setSelectedFedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleAllFeds = () => {
    setSelectedFedKeys(allFedSelected ? new Set() : new Set(lines.map(l => l.key)));
  };

  const metricLabel = metrics.find(m => m.key === metric)?.label ?? 'Select';
  const fedTriggerLabel = lines.length > 0
    ? `${secondDropdownLabel} (${selectedFedKeys.size}/${lines.length})`
    : secondDropdownLabel;
  const metricTooltip = useTruncationTooltip(metricLabel);
  const fedTooltip = useTruncationTooltip(fedTriggerLabel);

    const getRoutePath = () => {
      const basePath = '/organization-dashboard';
      switch (title) {
        case 'Federation wise\nSummary':
          return basePath + '/federation-details';
        case 'Organization wise\nSummary':
          return basePath + '/organization-details';
        case 'Ad Server\nSummary':
          return basePath + '/adserver-details';
        default:
          return basePath;
      }
    };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <div className={styles.headerActions}>
          {(() => {
            const disabled = !isLoading && (hasError || lines.length === 0);
            return (
              <UiTooltip text={disabled ? 'No data available' : 'Expand'} placement="top">
              <button
                className={styles.iconBtn}
                disabled={disabled}
                style={disabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                onClick={disabled ? undefined : () =>
                  history.push({
                    pathname: getRoutePath(),
                    state: {
                      ...(dateRange ? { dateRange } : {}),
                    },
                  })
                }
              >
                <ExpandIcon width={16} height={16} style={{ color: 'var(--Primary-Color-Shade-1)' }} />
              </button>
              </UiTooltip>
            );
          })()}

          {/* Metric dropdown */}
          <div className={styles.dropdownWrapper} ref={metricDropdownRef}>
            <button className={styles.dropdown} onClick={() => setMetricDropdownOpen(o => !o)}>
              <span
                ref={metricTooltip.ref}
                className={styles.dropdownLabel}
                title={metricTooltip.title}
              >
                {metricLabel}
              </span>
              <DownIcon width={10} height={6} style={{ color: '#092538', flexShrink: 0, marginTop: 3 }} />
            </button>
            {metricDropdownOpen && (
              <div className={styles.dropdownMenu}>
                {metrics.map(m => (
                  <button
                    key={m.key}
                    className={`${styles.dropdownMenuItem} ${metric === m.key ? styles.dropdownMenuItemActive : ''}`}
                    onClick={() => { setMetric(m.key); setMetricDropdownOpen(false); }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Federation / entity multiselect */}
          <div className={styles.dropdownWrapper} ref={fedDropdownRef}>
            <button className={styles.dropdown} onClick={() => setFedDropdownOpen(o => !o)}>
              <span
                ref={fedTooltip.ref}
                className={styles.dropdownLabel}
                title={fedTooltip.title}
              >
                {fedTriggerLabel}
              </span>
              <DownIcon width={10} height={6} style={{ color: '#092538', flexShrink: 0, marginTop: 3 }} />
            </button>
            {fedDropdownOpen && lines.length > 0 && (
              <div className={styles.fedDropdownMenu}>
                <div className={styles.fedDropdownScroll}>
                  <button className={styles.fedSelectAllRow} onClick={toggleAllFeds}>
                    <div className={`${styles.customCheckbox} ${allFedSelected ? styles.customCheckboxChecked : someFedSelected ? styles.customCheckboxIndeterminate : ''}`}>
                      {allFedSelected && <span className={styles.customCheckboxMark}>✓</span>}
                      {someFedSelected && <span className={styles.customCheckboxMark}>−</span>}
                    </div>
                    <span className={styles.fedSelectAllLabel}>Select All</span>
                  </button>
                  <div className={styles.fedDropdownDivider} />
                  {lines.map(l => {
                    const checked = selectedFedKeys.has(l.key);
                    return (
                      <button key={l.key} className={`${styles.fedItemRow} ${checked ? styles.fedItemRowSelected : ''}`} onClick={() => toggleFed(l.key)}>
                        <div className={`${styles.customCheckbox} ${checked ? styles.customCheckboxChecked : ''}`}>
                          {checked && <span className={styles.customCheckboxMark}>✓</span>}
                        </div>
                        <div className={styles.fedColorDot} style={{ backgroundColor: l.color }} />
                        <span className={styles.fedItemName}>{l.name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className={styles.fedDropdownFooter}>
                  <span className={styles.fedDropdownFooterCount}>{selectedFedKeys.size} of {lines.length} selected</span>
                  {someFedSelected && (
                    <button className={styles.fedDropdownFooterReset} onClick={() => setSelectedFedKeys(new Set(lines.map(l => l.key)))}>
                      Reset
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* <button className={styles.downloadBtn} title="Download">
            <img src={ChartDownloadIcon} width={26} height={26} alt="" />
          </button> */}
        </div>
      </div>

      <div className={styles.divider} />

      {isLoading ? (
        <div className={styles.shimmerArea}>
          <div className={`${styles.shimmerBlock} ${styles.shimmerChart}`} />
          <div className={styles.shimmerLegendRow}>
            {[1, 2].map(i => <div key={i} className={`${styles.shimmerBlock} ${styles.shimmerPill}`} />)}
          </div>
        </div>
      ) : hasError ? (
        <ErrorState />
      ) : lines.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className={styles.body}>
            <div className={styles.chartArea}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                data={data}
                margin={{ top: 8, right: 4, left: 0, bottom: 4 }}
                style={onMonthClick ? { cursor: 'pointer' } : undefined}
                onClick={(chartData: any) => {
                  if (onMonthClick && chartData?.activeLabel) {
                    onMonthClick(String(chartData.activeLabel), metric, Array.from(selectedFedKeys));
                  }
                }}
              >
                  <CartesianGrid strokeDasharray="2 2" stroke="#D4D4D8" />
                  <XAxis
                    dataKey={xAxisKey}
                    axisLine={false}
                    tickLine={false}
                    tick={onMonthClick
                      ? (props: any) => {
                          const { x, y, payload } = props;
                          return (
                            <g transform={`translate(${x},${y})`} style={{ cursor: 'pointer' }} onClick={() => onMonthClick(payload.value, metric, Array.from(selectedFedKeys))}>
                              <text x={0} y={0} dy={12} textAnchor="middle" fill="#0D6AB3" fontSize={10} fontWeight={500}>
                                {payload.value}
                              </text>
                            </g>
                          );
                        }
                      : { fontSize: 10, fill: '#71717a' }
                    }
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} width={40} tickFormatter={yTickFormatter} />
                  <Tooltip
                    wrapperStyle={{ zIndex: 1000 }}
                    allowEscapeViewBox={{ x: false, y: true }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const nonZero = payload
                        .filter((p: any) => p.value > 0)
                        .sort((a: any, b: any) => b.value - a.value);
                      const MAX = 6;
                      const shown = nonZero.slice(0, MAX);
                      const extra = nonZero.length - shown.length;
                      return (
                        <div style={{ background: 'white', border: 'none', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontSize: 10, minWidth: 170, maxWidth: 230, pointerEvents: 'none' }}>
                          <div style={{ padding: '6px 10px 5px', fontWeight: 600, color: '#092538', borderBottom: '1px solid #f1f5f9' }}>{label}</div>
                          <div style={{ padding: '4px 0' }}>
                            {shown.map((entry: any, i: number) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px' }}>
                                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
                                <span style={{ flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</span>
                                <span style={{ color: '#092538', fontWeight: 600, marginLeft: 8, flexShrink: 0 }}>{yTickFormatter ? yTickFormatter(entry.value) : entry.value}</span>
                              </div>
                            ))}
                            {extra > 0 && (
                              <div style={{ padding: '4px 10px 2px', color: '#94a3b8', fontStyle: 'italic' }}>+{extra} more (use dropdown to filter)</div>
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />
                  {lines.filter(l => selectedFedKeys.has(l.key)).map(line => (
                    <Line
                      key={`${line.key}_${metric}`}
                      type="monotone"
                      dataKey={`${line.key}_${metric}`}
                      name={line.name}
                      stroke={line.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

          </div>

          {onMonthClick && (
            <div style={{ padding: '0 var(--space-2sm) 4px', fontSize: 10, color: '#6f7a85' }}>
              Click a month to view daily breakdown
            </div>
          )}

          {/* Color key with scroll fades */}
          <div className={styles.colorKeyWrapper}>
            {fadeLeft  && <div className={`${styles.colorKeyFade} ${styles.colorKeyFadeLeft}`} />}
            {fadeRight && <div className={`${styles.colorKeyFade} ${styles.colorKeyFadeRight}`} />}
            <div
              className={`${styles.colorKeyRow} ${lines.length <= 4 ? styles.colorKeyRowCentered : ''}`}
              ref={colorKeyRef}
              onScroll={updateFades}
            >
              {lines.map(line => {
                const visible = selectedFedKeys.has(line.key);
                return (
                  <UiTooltip key={line.key} text={visible ? `Hide ${line.name}` : `Show ${line.name}`} placement="top">
                  <button
                    className={`${styles.colorKeyPill} ${!visible ? styles.colorKeyPillHidden : ''}`}
                    onClick={() => toggleFed(line.key)}
                  >
                    <span className={styles.colorKeyDot} style={{ backgroundColor: line.color }} />
                    <span className={styles.colorKeyName}>{line.name}</span>
                    {visible ? (
                      <span style={{ display: 'inline-flex', transform: 'rotate(45deg)', position: 'relative', top: -1 }}>
                        <SimplePlusIcon color="#64748b" size={8} />
                      </span>
                    ) : (
                      <SimplePlusIcon color="var(--Primary-Color-Shade-1)" size={8} />
                    )}
                  </button>
                  </UiTooltip>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FederationSummaryCard;
